import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';
import axios from 'axios';
import { useCheckout } from '../CheckoutContext';
import { useTheme } from '../ThemeContext';
import { useT } from '../i18n/useT';
import { useLang } from '../i18n/LanguageContext';
import { formatCallSlot, loadCallWindow } from '../lib/callSlots';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PortalBackground = ({ isDark }) => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    let animationId;
    let time = 0;

    const resize = () => { canvas.width = window.innerWidth; canvas.height = window.innerHeight; };
    resize();
    window.addEventListener('resize', resize);

    const color = isDark ? '0, 212, 255' : '27, 42, 74';
    const particles = Array.from({ length: 80 }, () => ({
      angle: Math.random() * Math.PI * 2,
      radius: 100 + Math.random() * 250,
      speed: 0.003 + Math.random() * 0.012,
      size: 0.8 + Math.random() * 2,
      alpha: 0.15 + Math.random() * 0.4,
    }));

    // Floating code fragments
    const codeFragments = [
      'OpenRoom.init()', 'await unlock()', 'level = "BOSS"', 'import { Elite }',
      'discipline: true', 'mindset: "VIP"', 'deploy(config)', 'formations: 6',
      'revenue.scale()', 'AI.automate()', 'const boss = {}', 'access: "VIP"',
    ];
    const floaters = Array.from({ length: 16 }, () => ({
      text: codeFragments[Math.floor(Math.random() * codeFragments.length)],
      x: Math.random() * 2000,
      y: Math.random() * 1200,
      speed: 0.15 + Math.random() * 0.3,
      alpha: 0.03 + Math.random() * 0.06,
      size: 10 + Math.random() * 4,
    }));

    const draw = () => {
      time += 0.016;
      const cx = canvas.width / 2;
      const cy = canvas.height / 2;

      ctx.fillStyle = isDark ? 'rgba(0, 0, 0, 0.08)' : 'rgba(249, 251, 252, 0.08)';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // Floating code text in background
      ctx.font = '13px monospace';
      for (const f of floaters) {
        f.y -= f.speed;
        if (f.y < -20) {
          f.y = canvas.height + 20;
          f.x = Math.random() * canvas.width;
          f.text = codeFragments[Math.floor(Math.random() * codeFragments.length)];
        }
        ctx.fillStyle = `rgba(${color}, ${f.alpha})`;
        ctx.fillText(f.text, f.x, f.y);
      }

      // Portal rings
      const portalRadius = Math.min(cx, cy) * 0.35;
      for (let r = 0; r < 3; r++) {
        const ringR = portalRadius * (0.7 + r * 0.15);
        const alpha = isDark ? (0.12 - r * 0.03) : (0.06 - r * 0.015);
        ctx.beginPath();
        ctx.arc(cx, cy, ringR, 0, Math.PI * 2);
        ctx.strokeStyle = `rgba(${color}, ${alpha})`;
        ctx.lineWidth = 1;
        ctx.stroke();
      }

      // Spinning dashes
      const dashCount = 20;
      for (let i = 0; i < dashCount; i++) {
        const a = (i / dashCount) * Math.PI * 2 + time * 0.4;
        const len = 6 + Math.sin(time * 2 + i) * 3;
        const x1 = cx + Math.cos(a) * portalRadius;
        const y1 = cy + Math.sin(a) * portalRadius;
        const x2 = cx + Math.cos(a) * (portalRadius + len);
        const y2 = cy + Math.sin(a) * (portalRadius + len);
        ctx.beginPath();
        ctx.moveTo(x1, y1);
        ctx.lineTo(x2, y2);
        ctx.strokeStyle = `rgba(${color}, ${isDark ? 0.15 : 0.08})`;
        ctx.lineWidth = 1;
        ctx.stroke();
      }

      // Orbiting particles
      for (const p of particles) {
        p.angle += p.speed;
        const px = cx + Math.cos(p.angle) * p.radius;
        const py = cy + Math.sin(p.angle) * (p.radius * 0.55);
        ctx.beginPath();
        ctx.arc(px, py, p.size, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${color}, ${p.alpha * (isDark ? 0.5 : 0.3)})`;
        ctx.fill();
      }

      // Center glow
      const grd = ctx.createRadialGradient(cx, cy, 0, cx, cy, portalRadius * 0.5);
      grd.addColorStop(0, `rgba(${color}, ${isDark ? 0.04 : 0.02})`);
      grd.addColorStop(1, `rgba(${color}, 0)`);
      ctx.fillStyle = grd;
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      animationId = requestAnimationFrame(draw);
    };

    draw();
    return () => { cancelAnimationFrame(animationId); window.removeEventListener('resize', resize); };
  }, [isDark]);

  return <canvas ref={canvasRef} className="absolute inset-0 z-0" />;
};

const LoginPage = ({ onLogin }) => {
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const { startCheckout, isActive } = useCheckout();
  const { lang } = useLang();
  const t = useT();
  const [step, setStep] = useState('email');
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');

  // Colors based on theme
  const accent = isDark ? '#00D4FF' : '#1B2A4A';
  const pageBg = isDark ? '#000000' : '#F9FBFC';
  const cardBg = isDark ? 'rgba(5, 5, 15, 0.85)' : 'rgba(255, 255, 255, 0.88)';
  const cardBorder = isDark ? 'rgba(0, 212, 255, 0.15)' : 'rgba(27, 42, 74, 0.12)';
  const cardShadow = isDark
    ? '0 0 40px rgba(0, 212, 255, 0.06), inset 0 1px 0 rgba(255,255,255,0.04)'
    : '0 8px 40px rgba(27, 42, 74, 0.1), inset 0 1px 0 rgba(255,255,255,0.8)';
  const textPrimary = isDark ? '#FFFFFF' : '#1B2A4A';
  const textSecondary = isDark ? 'rgba(255,255,255,0.4)' : 'rgba(27, 42, 74, 0.5)';
  const inputBg = isDark ? 'rgba(0, 0, 0, 0.5)' : 'rgba(249, 251, 252, 0.8)';
  const inputBorder = isDark ? 'rgba(0, 212, 255, 0.12)' : 'rgba(27, 42, 74, 0.15)';
  const inputBorderFocus = isDark ? 'rgba(0, 212, 255, 0.4)' : 'rgba(27, 42, 74, 0.4)';
  const inputText = isDark ? '#FFFFFF' : '#1B2A4A';
  const btnBg = isDark ? '#00D4FF' : '#1B2A4A';
  const btnText = isDark ? '#000000' : '#FFFFFF';
  const btnGlow = isDark ? '0 0 20px rgba(0, 212, 255, 0.2)' : '0 4px 15px rgba(27, 42, 74, 0.2)';
  const btnGlowHover = isDark ? '0 0 30px rgba(0, 212, 255, 0.4)' : '0 4px 20px rgba(27, 42, 74, 0.35)';
  const linkColor = isDark ? '#00D4FF' : '#1B2A4A';
  const glowBg = isDark
    ? 'radial-gradient(circle, rgba(0,212,255,0.08) 0%, transparent 70%)'
    : 'radial-gradient(circle, rgba(27,42,74,0.04) 0%, transparent 70%)';
  const logoGlow = isDark ? '0 0 15px rgba(0,212,255,0.2)' : '0 0 15px rgba(27,42,74,0.08)';

  const handleSendOtp = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/send-otp`, { email, locale: lang });
      setSuccess(response.data.message || t.login.sentDefault);
      setStep('otp');
    } catch (err) {
      // Le Worker répond dans la langue envoyée (`locale`).
      setError(err.response?.data?.detail || t.login.sendError);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/verify-otp`, { email, otp, locale: lang });
      const { access_token, user } = response.data;
      localStorage.setItem('token', access_token);
      localStorage.setItem('user', JSON.stringify(user));
      if (onLogin) onLogin(user);
      navigate('/dashboard');
    } catch (err) {
      setError(err.response?.data?.detail || t.login.verifyError);
    } finally {
      setLoading(false);
    }
  };

  const joinOpenBro = () => {
    if (isActive) return;
    const callWindow = loadCallWindow();
    const callAt = callWindow.selected;
    startCheckout('login_join', {
      callAt,
      callLabel: formatCallSlot(callAt, lang),
      callTz: Intl.DateTimeFormat().resolvedOptions().timeZone,
    });
  };

  return (
    <div className="min-h-screen flex items-center justify-center relative overflow-hidden transition-colors duration-500" style={{ backgroundColor: pageBg }}>
      {/* Portal Background */}
      <PortalBackground isDark={isDark} />

      {/* Radial glow */}
      <div className="absolute z-[1] w-[500px] h-[500px] rounded-full" style={{ background: glowBg, top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }} />

      {/* Login Card */}
      <div className="relative z-10 w-full max-w-[440px] mx-4">
        <div
          className="rounded-xl p-8 md:p-10 transition-all duration-500"
          style={{
            backgroundColor: cardBg,
            backdropFilter: 'blur(20px)',
            border: `1px solid ${cardBorder}`,
            boxShadow: cardShadow,
          }}
        >
          {/* Back → Stripe */}
          <div
            onClick={() => startCheckout('login_back')}
            data-testid="login-back-button"
            className="flex items-center gap-1 text-[14px] hover:opacity-80 transition-all mb-8 group cursor-pointer"
            style={{ fontFamily: '"Inter", sans-serif', color: linkColor }}
          >
            <ChevronLeft size={18} className="group-hover:-translate-x-1 transition-transform" />
            {t.login.back}
          </div>

          {/* Logo */}
          <div className="text-center mb-8">
            <img src="/logo-openbro.png" alt="OpenBro" className="h-[44px] w-auto mx-auto mb-4 object-contain" />
            <h2 className="text-[22px] font-bold transition-colors duration-300" style={{ fontFamily: '"Space Grotesk", sans-serif', color: textPrimary }}>
              {t.login.title}
            </h2>
            <p className="text-[13px] mt-2 transition-colors duration-300" style={{ fontFamily: '"Inter", sans-serif', color: textSecondary }}>
              {t.login.subtitle}
            </p>
          </div>

          {/* Error */}
          {error && (
            <div data-testid="login-error" className="px-4 py-3 rounded-lg mb-4 text-[13px]" style={{ backgroundColor: 'rgba(239, 68, 68, 0.1)', border: '1px solid rgba(239, 68, 68, 0.3)', color: '#f87171', fontFamily: '"Inter", sans-serif' }}>
              {error}
            </div>
          )}

          {/* Success */}
          {success && step === 'otp' && (
            <div data-testid="login-success" className="px-4 py-3 rounded-lg mb-4 text-[13px]" style={{ backgroundColor: isDark ? 'rgba(0, 212, 255, 0.08)' : 'rgba(27, 42, 74, 0.06)', border: `1px solid ${isDark ? 'rgba(0, 212, 255, 0.2)' : 'rgba(27, 42, 74, 0.15)'}`, color: accent, fontFamily: '"Inter", sans-serif' }}>
              {success}
            </div>
          )}

          {step === 'email' ? (
            <form onSubmit={handleSendOtp} className="space-y-4">
              <div>
                <label className="block text-[12px] uppercase tracking-wider mb-2 transition-colors duration-300" style={{ fontFamily: '"Inter", sans-serif', color: textSecondary }}>
                  {t.login.emailLabel}
                </label>
                <input
                  type="email"
                  placeholder={t.login.emailExample}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  data-testid="login-email-input"
                  className="w-full px-4 py-3.5 rounded-lg text-[15px] focus:outline-none transition-all duration-300"
                  style={{
                    backgroundColor: inputBg,
                    border: `1px solid ${inputBorder}`,
                    color: inputText,
                    fontFamily: '"Inter", sans-serif',
                  }}
                  onFocus={(e) => e.target.style.borderColor = inputBorderFocus}
                  onBlur={(e) => e.target.style.borderColor = inputBorder}
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                data-testid="login-send-otp"
                className="w-full py-3.5 rounded-lg font-bold text-[14px] uppercase tracking-wider transition-all duration-300 disabled:opacity-40"
                style={{ backgroundColor: btnBg, color: btnText, fontFamily: '"Inter", sans-serif', boxShadow: btnGlow }}
                onMouseEnter={(e) => e.target.style.boxShadow = btnGlowHover}
                onMouseLeave={(e) => e.target.style.boxShadow = btnGlow}
              >
                {loading ? t.login.sending : t.login.sendOtp}
              </button>
            </form>
          ) : (
            <form onSubmit={handleVerifyOtp} className="space-y-4">
              <p className="text-center text-[13px] mb-2 transition-colors duration-300" style={{ fontFamily: '"Inter", sans-serif', color: textSecondary }}>
                {t.login.codeSent} <span className="font-bold" style={{ color: accent }}>{email}</span>
              </p>

              <div>
                <label className="block text-[12px] uppercase tracking-wider mb-2 transition-colors duration-300" style={{ fontFamily: '"Inter", sans-serif', color: textSecondary }}>
                  {t.login.codeLabel}
                </label>
                <input
                  type="text"
                  placeholder="000000"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value)}
                  required
                  maxLength={6}
                  data-testid="login-otp-input"
                  className="w-full px-4 py-3.5 rounded-lg text-center tracking-[0.5em] text-[22px] font-bold focus:outline-none transition-all duration-300"
                  style={{
                    backgroundColor: inputBg,
                    border: `1px solid ${inputBorder}`,
                    color: inputText,
                    fontFamily: 'monospace',
                  }}
                  onFocus={(e) => e.target.style.borderColor = inputBorderFocus}
                  onBlur={(e) => e.target.style.borderColor = inputBorder}
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                data-testid="login-verify-otp"
                className="w-full py-3.5 rounded-lg font-bold text-[14px] uppercase tracking-wider transition-all duration-300 disabled:opacity-40"
                style={{ backgroundColor: btnBg, color: btnText, fontFamily: '"Inter", sans-serif', boxShadow: btnGlow }}
                onMouseEnter={(e) => e.target.style.boxShadow = btnGlowHover}
                onMouseLeave={(e) => e.target.style.boxShadow = btnGlow}
              >
                {loading ? t.login.verifying : t.login.verify}
              </button>

              <div className="flex items-center justify-between mt-2">
                <button type="button" onClick={handleSendOtp} className="text-[12px] hover:underline transition-all bg-transparent border-none cursor-pointer" style={{ color: linkColor, fontFamily: '"Inter", sans-serif' }}>
                  {t.login.resend}
                </button>
                <button type="button" onClick={() => { setStep('email'); setOtp(''); setError(''); setSuccess(''); }} className="text-[12px] hover:underline transition-all bg-transparent border-none cursor-pointer" style={{ color: textSecondary, fontFamily: '"Inter", sans-serif' }}>
                  {t.login.changeEmail}
                </button>
              </div>
            </form>
          )}
        </div>

        <p className="text-center mt-6 text-[12px] transition-colors duration-300" style={{ fontFamily: '"Inter", sans-serif', color: textSecondary }}>
          {t.login.noAccount}{' '}
          <button type="button" onClick={joinOpenBro} disabled={isActive} className="underline font-medium transition-colors cursor-pointer bg-transparent border-none p-0" style={{ color: linkColor, fontFamily: '"Inter", sans-serif' }}>
            {t.login.join}
          </button>
        </p>
      </div>
    </div>
  );
};

export default LoginPage;
